import { baseUrl, cartUrl, searchResultUrl, productsUrl } from '@src/app/routes/paths';

import { product_product_variants } from '@components/ProductPage/ProductDetailPage/__generated__/product';
import { productThumbnail_variants } from '@components/ProductPage/ProductThumbnail/__generated__/productThumbnail';
import { cart_cart_subcarts_lines_variant } from '@src/pages/Cart/__generated__/cart';
import { recommendedProducts_product_recommendedProducts } from '@src/components/ProductPage/ProductDetailRecommendation/__generated__/recommendedProducts';
import { getDBIdFromGraphqlId } from '@src/core/utils';
import { BEAUTY_DEVICE_PRODUCT_IDS } from './constants';
import { AddCartModalProduct } from './types';

export const groupBrandListByFirstLetter = (brandList) => {
  let brandListHash = {};

  brandList?.forEach((brand) => {
    brand?.forEach((item) => {
      let key = item?.name[0];
      key = key.match(/[^a-zA-Z]/i) ? '#' : key.toUpperCase();

      if (!brandListHash[key]) {
        brandListHash[key] = [];
      }
      brandListHash[key].push(item);
    });
  });

  return brandListHash;
};

export function addRecentlyViewedProducts(productId: number, thumbnailUrl: string, urlForGraphql: string) {
  let recentlyViewedProducts = JSON.parse(localStorage.getItem('recent-products')) || [];

  recentlyViewedProducts = recentlyViewedProducts.filter((product) => product?.id !== productId);

  const newProduct = { id: productId, thumbnailUrl, urlForGraphql };

  recentlyViewedProducts = [newProduct, ...recentlyViewedProducts];

  if (recentlyViewedProducts.length > 20) {
    recentlyViewedProducts = recentlyViewedProducts.slice(0, 20);
  }

  localStorage.setItem('recent-products', JSON.stringify(recentlyViewedProducts));
}

export function removeRecentlyViewedProduct(productId: number) {
  let recentlyViewedProducts = JSON.parse(localStorage.getItem('recent-products')) || [];

  recentlyViewedProducts = recentlyViewedProducts.filter((product) => product?.id !== productId);

  localStorage.setItem('recent-products', JSON.stringify(recentlyViewedProducts));
}

// 배열을 chunkSize 크기만큼 잘라서 새로운 배열로 만들어 return
export function chunkArray(inputArray, chunkSize) {
  const resultArray = [];

  for (let i = 0; i < inputArray.length; i += chunkSize) {
    resultArray.push({ subArray: inputArray.slice(i, i + chunkSize) });
  }

  return resultArray;
}

export function replaceAmpersandToUnderscore(str: string) {
  const resultString = str.replace(/&/g, '_');

  return resultString;
}

export function replaceUnderscoreWithSpace(str: string) {
  return str.replace(/_/g, ' ');
}

export function capitalizeFirstWord(str: string) {
  const words = str.split(' ');

  const capitalizedWords = words.map((word) => {
    const firstCapitalizedLetter = word.charAt(0).toUpperCase();

    const restOfWord = word.slice(1);

    return firstCapitalizedLetter + restOfWord;
  });

  const finalString = capitalizedWords.join(' ');

  return finalString;
}

export function isNew(date) {
  if (date) {
    const now = new Date();
    const diff = 60 * 60 * 24 * 28 * 1000;
    const publicationDateString = date.split('-');
    const publicationDate = new Date(publicationDateString[0], publicationDateString[1] - 1, publicationDateString[2]);

    now.setHours(0, 0, 0, 0);
    publicationDate.setHours(0, 0, 0, 0);

    if (now.getTime() - publicationDate.getTime() <= diff) {
      return true;
    } else {
      return false;
    }
  }
  return false;
}

export function isWithinMinutes(dateString, minutes) {
  if (dateString) {
    const givenTime = new Date(dateString);

    const localCurrentTimeLocal = new Date();

    const timeDifference = Math.abs(localCurrentTimeLocal.getTime() - givenTime.getTime());

    // 차이가 minutes분 이내인지 확인 (1분 = 60000)
    const millisecondsInMinutes = minutes * 60000;

    return timeDifference <= millisecondsInMinutes;
  }

  return false;
}

// url에서 DB ID 가져오기
export function extractIdFromUrl(url) {
  const matches = url.match(/(\d+)\/?$/);
  return matches ? parseInt(matches[1]) : null;
}

export function getPathFromUrl(url) {
  const parsedUrl = new URL(url);

  return parsedUrl.pathname;
}

export function extractTagNameFromPath(path: string) {
  const parts = path.split('/');

  const tagNameText = parts[parts.length - 1];
  const tagWords = tagNameText.split('-');
  tagWords.pop();

  return tagWords.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export function makeExhibitionUrl(exhibitionName: string, exhibitionID: string) {
  const removeSpecialCharacters = exhibitionName.replace(/[^a-zA-Z0-9 ]/g, '');

  const exhibitionNameToLowerCase = removeSpecialCharacters.toLowerCase();

  let convertExhibitionNameSpacesToHyphens = exhibitionNameToLowerCase.replace(/\s/g, '-');

  const exhibitionDetailUrl = `${productsUrl}exhibitions/${convertExhibitionNameSpacesToHyphens}-${exhibitionID}/`;

  return exhibitionDetailUrl;
}

export function makeCollectionUrl(collectionSlug: string, collectionID: string) {
  const collectionDetailUrl = `${productsUrl}collection/${collectionSlug}-${collectionID}/`;

  return collectionDetailUrl;
}

export function parsingCategoryData(data) {
  if (!data || data?.length === 0) {
    return [];
  }

  let parsedData = [];

  data?.forEach((zeroLevelItem) => {
    // level 0
    const level0Obj = {
      id: extractIdFromUrl(zeroLevelItem.url),
      name: zeroLevelItem.name,
      url: zeroLevelItem.url,
      level: 0,
    };

    parsedData.push({
      ...level0Obj,
      level0: level0Obj,
      level1:
        zeroLevelItem.child_items?.map((oneLevelItem) => ({
          id: extractIdFromUrl(oneLevelItem.url),
          name: oneLevelItem.name,
          url: oneLevelItem.url,
          child_items:
            oneLevelItem.child_items.map((twoLevelItem) => ({
              parentId: extractIdFromUrl(oneLevelItem.url),
              ...twoLevelItem,
            })) || [],
        })) || [],
    });

    // level 1
    zeroLevelItem.child_items?.forEach((oneLevelItem) => {
      const level1Obj = {
        id: extractIdFromUrl(oneLevelItem.url),
        name: oneLevelItem.name,
        url: oneLevelItem.url,
        level: 1,
      };

      parsedData.push({
        ...level1Obj,
        level0: level0Obj,
        level1: level1Obj,
        level2: oneLevelItem?.child_items || [],
      });

      // level 2
      oneLevelItem?.child_items?.forEach((twoLevelItem) => {
        const level2Obj = {
          id: extractIdFromUrl(twoLevelItem.url),
          name: twoLevelItem.name,
          url: twoLevelItem.url,
          level: 2,
        };

        parsedData.push({
          ...level2Obj,
          level0: level0Obj,
          level1: level1Obj,
          level2: oneLevelItem?.child_items || [],
        });
      });
    });
  });

  return parsedData;
}

export function parsingBrandCategoryData(brandCategories) {
  if (!brandCategories || brandCategories?.length === 0) {
    return [];
  }

  const transformedData = [];

  const groupedData = brandCategories.reduce((accumulator, currentCategory) => {
    const parentId = currentCategory.node.parent.id;

    if (!accumulator[parentId]) {
      accumulator[parentId] = {
        id: parentId,
        name: currentCategory.node.parent.name,
        child: [],
        totalCount: 0,
      };
    }

    accumulator[parentId].child.push({
      id: currentCategory.node.id,
      name: currentCategory.node.name,
      totalCount: currentCategory.node.products.totalCount,
    });

    accumulator[parentId].totalCount += currentCategory.node.products.totalCount;

    return accumulator;
  }, {});

  for (const parentId in groupedData) {
    transformedData.push(groupedData[parentId]);
  }

  transformedData.sort((a, b) => a.name.localeCompare(b.name));

  transformedData.forEach((parent) => {
    parent.child.sort((a, b) => a.name.localeCompare(b.name));
  });

  return transformedData;
}

export function findBrandByName(brandList, searchValue: string) {
  if (brandList) {
    const findBrand = brandList.find((brand) => brand.name.toLowerCase() === searchValue.toLowerCase());

    return findBrand;
  } else {
    return false;
  }
}

export type AllProduct = AddCartModalProduct | recommendedProducts_product_recommendedProducts;
// Price 관련 util
export function getPriceInfoFromProduct(product: AllProduct | AddCartModalProduct) {
  const originalPrice = product?.price?.amount || 0;
  const discountPrice = product?.availability?.priceRange?.start?.gross?.amount || 0;
  const finalPrice = discountPrice || originalPrice;
  const discountPercent = Math.round((100 * (originalPrice - discountPrice)) / originalPrice);
  const isOnSale = product?.availability?.onSale && originalPrice !== discountPrice;
  const isOutOfStock = product?.availability?.isOutOfStock;

  return {
    originalPrice,
    discountPrice,
    finalPrice,
    discountPercent,
    isOnSale,
    isOutOfStock,
  };
}

export function getPriceInfoFromVariant(
  variant: product_product_variants | productThumbnail_variants | cart_cart_subcarts_lines_variant,
) {
  const originalPrice = variant?.price?.amount || 0;
  const discountPrice = variant?.discountedPrice?.amount || 0;
  const finalPrice = discountPrice || originalPrice;
  const discountPercent = Math.round((100 * (originalPrice - discountPrice)) / originalPrice);
  const isOnSale = originalPrice !== discountPrice;
  const isOutOfStock = variant?.stockQuantity < 0;
  const isEndOfLife = variant?.isEndOfLife;
  const isPossibleSale = isEndOfLife && !isOutOfStock; // 단종되었지만 재고가 있는 경우.

  return {
    originalPrice,
    discountPrice,
    finalPrice,
    discountPercent,
    isOnSale,
    isOutOfStock,
    isPossibleSale,
  };
}

export function formatPrice(
  amount: number,
  unit: string = '₹',
  fraction: boolean = true,
  minimumFractionDigits: number = 2,
) {
  let result = '';

  if (fraction) {
    result += amount.toLocaleString('en-US', {
      minimumFractionDigits,
      maximumFractionDigits: 2,
    });
  } else {
    result += amount.toLocaleString('en-US');
  }

  if (unit) {
    result = unit + result;
  }

  return result;
}

export function caculateSimplPrice(price: number) {
  const div = price / 3;
  const pow = Math.pow(10, 2);

  const final = Math.round(div * pow) / pow;

  return final;
}

export function caculateBeautyDeviceSimplPrice(price: number) {
  const div = price / 24;
  const pow = Math.pow(10, 2);

  const final = Math.round(div * pow) / pow;

  return final;
}

export function formatDate(dateString: string, splitWord = '.') {
  const date = new Date(dateString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  const dateArr = [year, month, day];
  const dateStr = dateArr.join(splitWord);

  return dateStr;
}

export function formatWeight(weight: number, quantity: number) {
  const totalWeight = (weight * quantity).toPrecision(4);

  return parseFloat(totalWeight).toString();
}

export function getSelectedProductIdsFromSubCart(subCarts) {
  if (subCarts) {
    const selectedProductIds = [];

    subCarts.forEach((subCart) => {
      subCart.lines.forEach((line) => {
        if (line.selected) {
          selectedProductIds.push(String(getDBIdFromGraphqlId(line.variant.product.id)));
        }
      });
    });

    return selectedProductIds;
  } else {
    return [];
  }
}

export function isBeautyDeviceProductInSelectedCart(selectedProductIds) {
  return selectedProductIds.some((id) => BEAUTY_DEVICE_PRODUCT_IDS.includes(id));
}
